import { Navigate, Outlet, createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Shell } from "../shell/Shell";
import { Stub } from "../shell/Stub";
import { LoginPage } from "../features/login/LoginPage";
import { HoyPage } from "../features/hoy/HoyPage";
import { SkillsPage } from "../features/skills/SkillsPage";
import { SkillPage } from "../features/skills/SkillPage";
import { ImplantacionesPage } from "../features/implantaciones/ImplantacionesPage";
import { BibliotecaPage } from "../features/conocimiento/BibliotecaPage";
import { FichaPage } from "../features/conocimiento/FichaPage";
import { leerBusqueda } from "../features/conocimiento/logica";
import { ImplantacionPage } from "../features/implantaciones/ImplantacionPage";
import { ColaPage } from "../features/cola/ColaPage";
import { CapacidadesPage } from "../features/capacidades/CapacidadesPage";
import { AlertasPage } from "../features/alertas/AlertasPage";
import { AuditoriaPage } from "../features/auditoria/AuditoriaPage";
import { EquipoPage } from "../features/equipo/EquipoPage";
import { SimuladorPage } from "../features/simulador/SimuladorPage";
import { ExploradorPage } from "../features/explorador/ExploradorPage";
import { leerSearch } from "../features/implantaciones/episodios";

const rootRoute = createRootRoute({ component: Outlet });

/** Puerta: sin sesión → /login; con MSAL resolviendo el redirect, espera. */
function Gate() {
  const authed = useIsAuthenticated();
  const { inProgress } = useMsal();
  if (inProgress !== InteractionStatus.None && !authed) return null;
  return authed ? <Shell /> : <Navigate to="/login" />;
}
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: () => (useIsAuthenticated() ? <Navigate to="/" /> : <LoginPage />) });
const shellRoute = createRoute({ getParentRoute: () => rootRoute, id: "shell", component: Gate });

/** Rutas con path LITERAL (el router las tipa); la nav (shell/nav.ts) es el registro y un test comprueba que coinciden. */
const stub = (label: string, prototype: string) => () => <Stub title={label} prototype={prototype} />;
const page = <P extends string>(path: P, component: () => React.JSX.Element) => createRoute({ getParentRoute: () => shellRoute, path, component });

export const ROUTES = [
  page("/", HoyPage),
  page("/skills", SkillsPage),
  page("/skills/$slug", SkillPage),
  // la biblioteca (ADR-14): `?q=Cnn` abre la tabla en ese cuaderno; `?estado=` y `?firma=` fuerzan estados para revisión
  createRoute({ getParentRoute: () => shellRoute, path: "/conocimiento", component: BibliotecaPage, validateSearch: leerBusqueda }),
  createRoute({ getParentRoute: () => shellRoute, path: "/conocimiento/$slug", component: FichaPage, validateSearch: leerBusqueda }),
  page("/capacidades", CapacidadesPage),
  page("/implantaciones", ImplantacionesPage),
  // con search propio: el inspector guarda ahí su selección
  // (`?ep=&turno=`, junto al conmutador dev `?estado=`) para poder enlazar
  // un mensaje concreto
  createRoute({ getParentRoute: () => shellRoute, path: "/implantaciones/$id", component: ImplantacionPage, validateSearch: leerSearch }),
  page("/cola", ColaPage),
  page("/alertas", AlertasPage),
  page("/auditoria", AuditoriaPage),
  page("/equipo", EquipoPage),
  page("/simulador", SimuladorPage),
  // el explorador (spike de I-18): cuelga del simulador, sin prototipo propio
  page("/simulador/explorar", ExploradorPage),
];
const routeTree = rootRoute.addChildren([loginRoute, shellRoute.addChildren(ROUTES)]);

export const router = createRouter({ routeTree });
declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}
